
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, Eye, EyeOff, Menu, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { formatCPF, validateCPF } from "@/utils/cpfValidation";
import { supabase } from "@/integrations/supabase/client";
import { maskEmail } from "@/lib/utils";

const Login = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loginType, setLoginType] = useState<'email' | 'cpf'>('email');
  const [lastLoginError, setLastLoginError] = useState<string | null>(null);
  const [resolvedEmail, setResolvedEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const { signIn, user } = useAuth();

  // Redirect if already logged in - let ProtectedRoute handle navigation
  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleIdentifierChange = (value: string) => {
    if (loginType === 'cpf') {
      setIdentifier(formatCPF(value));
    } else {
      setIdentifier(value);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLastLoginError(null);
    setResolvedEmail(null);

    if (!identifier || !password) {
      toast.error("Preencha todos os campos");
      return;
    }

    // Validate CPF if login type is CPF
    if (loginType === 'cpf' && !validateCPF(identifier)) {
      toast.error("CPF inválido");
      return;
    }

    setLoading(true);

    try {
      const { error } = await signIn(identifier, password);

      if (error) {
        console.error('Login error received:', error);

        // For CPF login with invalid credentials, resolve email for password reset option
        if (loginType === "cpf" && error.includes("Invalid login credentials")) {
          try {
            const cleanCpf = identifier.replace(/\D/g, '');
            const { data: email } = await supabase.rpc('get_email_by_cpf', { input_cpf: cleanCpf });
            if (email) {
              setResolvedEmail(email);
              setLastLoginError("Senha incorreta para este CPF");
            } else {
              setLastLoginError(error);
            }
          } catch {
            setLastLoginError(error);
          }
        } else {
          setLastLoginError(error);
        }

        toast.error(error);
      } else {
        toast.success("Login realizado com sucesso");
        // Remove manual navigation - let useAuth and ProtectedRoute handle it
        // The useEffect above will handle redirect when user state updates
      }
    } catch (error) {
      console.error('Login error:', error);
      setLastLoginError("Erro inesperado no login");
      toast.error("Erro inesperado no login");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (loginType === "cpf") {
      const cleanCpf = identifier.replace(/\D/g, '');
      if (cleanCpf.length !== 11) {
        toast.error("Digite um CPF válido");
        return;
      }

      try {
        setLoading(true);
        const { data: email } = await supabase.rpc('get_email_by_cpf', { input_cpf: cleanCpf });

        if (!email) {
          toast.error("CPF não encontrado ou perfil inativo");
          return;
        }

        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`
        });

        if (error) {
          toast.error("Erro ao enviar email de redefinição");
          return;
        }

        toast.success(`Link de redefinição enviado para ${maskEmail(email)}`);
      } catch (error) {
        toast.error("Erro ao processar solicitação");
      } finally {
        setLoading(false);
      }
    } else {
      // Email mode
      if (!identifier.includes('@')) {
        toast.error("Digite um email válido");
        return;
      }

      try {
        setLoading(true);
        const { error } = await supabase.auth.resetPasswordForEmail(identifier, {
          redirectTo: `${window.location.origin}/reset-password`
        });

        if (error) {
          toast.error("Erro ao enviar email de redefinição");
          return;
        }

        toast.success(`Link de redefinição enviado para ${maskEmail(identifier)}`);
      } catch (error) {
        toast.error("Erro ao processar solicitação");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMagicLink = async () => {
    if (loginType === "cpf") {
      const cleanCpf = identifier.replace(/\D/g, '');
      if (cleanCpf.length !== 11) {
        toast.error("Digite um CPF válido");
        return;
      }

      try {
        setLoading(true);
        const { data: email } = await supabase.rpc('get_email_by_cpf', { input_cpf: cleanCpf });

        if (!email) {
          toast.error("CPF não encontrado ou perfil inativo");
          return;
        }

        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/`
          }
        });

        if (error) {
          toast.error("Erro ao enviar link mágico");
          return;
        }

        toast.success(`Link de acesso enviado para ${maskEmail(email)}. Verifique seu email.`);
      } catch (error) {
        toast.error("Erro ao processar solicitação");
      } finally {
        setLoading(false);
      }
    } else {
      // Email mode
      if (!identifier.includes('@')) {
        toast.error("Digite um email válido");
        return;
      }

      try {
        setLoading(true);
        const { error } = await supabase.auth.signInWithOtp({
          email: identifier,
          options: {
            emailRedirectTo: `${window.location.origin}/`
          }
        });

        if (error) {
          toast.error("Erro ao enviar link mágico");
          return;
        }

        toast.success(`Link de acesso enviado para ${maskEmail(identifier)}. Verifique seu email.`);
      } catch (error) {
        toast.error("Erro ao processar solicitação");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleWhatsAppContact = () => {
    const message = "Olá! Gostaria de conhecer mais sobre o itaCondo.";
    const whatsappUrl = `https://wa.me/5521998231720?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="fixed top-4 left-4 right-4 z-50 bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">itaCondo</h1>
                <p className="text-xs text-blue-300/80">Sistemas</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/landing" className="text-slate-300 hover:text-white transition-colors">Início</Link>
              <Link to="/landing#features" className="text-slate-300 hover:text-white transition-colors">Funcionalidades</Link>
              <Link to="/landing#testimonials" className="text-slate-300 hover:text-white transition-colors">Depoimentos</Link>
              <Link to="/landing#faq" className="text-slate-300 hover:text-white transition-colors">FAQ</Link>
              <Link to="/landing#contact" className="text-slate-300 hover:text-white transition-colors">Contato</Link>
              



            </nav>

            {/* Mobile menu button */}
            <button className="md:hidden text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen &&
          <div className="md:hidden py-4 border-t border-white/10">
              <div className="flex flex-col space-y-4">
                <Link to="/landing" className="text-slate-300 hover:text-white" onClick={() => setIsMenuOpen(false)}>Início</Link>
                <Link to="/landing#features" className="text-slate-300 hover:text-white" onClick={() => setIsMenuOpen(false)}>Funcionalidades</Link>
                <Link to="/landing#testimonials" className="text-slate-300 hover:text-white" onClick={() => setIsMenuOpen(false)}>Depoimentos</Link>
                <Link to="/landing#faq" className="text-slate-300 hover:text-white" onClick={() => setIsMenuOpen(false)}>FAQ</Link>
                <Link to="/landing#contact" className="text-slate-300 hover:text-white" onClick={() => setIsMenuOpen(false)}>Contato</Link>
                <Button onClick={() => {handleWhatsAppContact();setIsMenuOpen(false);}} size="sm" className="bg-whatsapp text-whatsapp-foreground hover:bg-whatsapp/90">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          }
        </div>
      </header>

      {/* Login Content */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4 pt-24 min-h-screen">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
        <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">itaCondo</h1>
              <p className="text-xs text-blue-300/80">Sistemas</p>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-white">Acesse sua conta</h2>
          <p className="text-slate-400">Entre com suas credenciais</p>
        </div>

        <Card className="bg-slate-800/50 border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-white">Fazer Login</CardTitle>
            <CardDescription className="text-slate-400">Digite seu {loginType === 'email' ? 'email' : 'CPF'} e senha para acessar o sistema</CardDescription>
            
            {/* Login type toggle */}
            <div className="flex gap-2 mt-4">
              <Button
                  type="button"
                  variant={loginType === 'email' ? 'default' : 'outline'}
                  onClick={() => {
                    setLoginType('email');
                    setIdentifier('');
                  }}
                  className="flex-1"
                  size="sm">

                Email
              </Button>
              <Button
                  type="button"
                  variant={loginType === 'cpf' ? 'default' : 'outline'}
                  onClick={() => {
                    setLoginType('cpf');
                    setIdentifier('');
                  }}
                  className="flex-1"
                  size="sm">

                CPF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-slate-300">
                  {loginType === 'email' ? 'Email' : 'CPF'}
                </Label>
                <Input
                    id="identifier"
                    type={loginType === 'email' ? 'email' : 'text'}
                    placeholder={loginType === 'email' ? 'seu@email.com' : '000.000.000-00'}
                    value={identifier}
                    onChange={(e) => handleIdentifierChange(e.target.value)}
                    required
                    maxLength={loginType === 'cpf' ? 14 : undefined}
                    className="bg-slate-700/50 border-white/10 text-white placeholder:text-slate-500" />

              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Senha</Label>
                <div className="relative">
                  <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-slate-700/50 border-white/10 text-white placeholder:text-slate-500" />

                  <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>

                    {showPassword ?
                      <EyeOff className="h-4 w-4 text-slate-400" /> :

                      <Eye className="h-4 w-4 text-slate-400" />
                      }
                  </Button>
                </div>
              </div>
              <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}>

                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              {/* Password reset error feedback */}
              {lastLoginError && resolvedEmail &&
                <div className="text-center p-3 bg-red-900/20 rounded-md border border-red-500/30">
                  <p className="text-sm text-red-600 mb-2">{lastLoginError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handlePasswordReset}
                    disabled={loading}
                    className="border-red-300 text-red-600 hover:bg-red-50">

                    Enviar link de redefinição para {maskEmail(resolvedEmail)}
                  </Button>
                </div>
                }

              {/* Password reset and magic link options */}
              <div className="space-y-2">
                








                
                








              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center space-y-2">
          <p className="text-sm text-slate-500">
            Esqueceu a senha?{' '}
            <Link to="/forgot-password" className="text-blue-400 hover:underline">
              Recuperar senha
            </Link>
          </p>
        </div>
        </div>
      </div>
    </div>);

};

export default Login;